import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import {
  hashQueryKey,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { Link, RouterProvider } from '@tanstack/react-router';
import { NavProvider } from '@teamworksdev/nav';
import { Provider as JotaiProvider } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AuthProvider, useAuth } from '../auth';
import { router } from '../router';
import {
  getTeamIdsHeader,
  getTrpcClientOptions,
  isClientRoutePermissionError,
} from '../trpc/common';
import { trpc } from '../trpc/react';
import { Toaster } from './components/toast/toaster';
import { cn } from './components/utils';
import { FeatureFlagsProvider, useFlags } from './context/featureFlag';
import {
  readStoredSelectedTeam,
  writeStoredSelectedTeam,
} from './context/selected-team-storage';
import { ViewerProvider } from './context/viewer';
import { initPageTracking } from '../reporting/page-tracking';
import { getLocale } from './utils/i18n';
import { jotaiStore } from './utils/jotai';
import { ZeroClientProvider } from './zero/ZeroClientProvider';

// Procedures whose team scope (if any) is encoded in their input rather than
// the tw-team-ids header. The queryKeyHashFn team suffix is redundant for these
// since React Query's cache key already varies by input — adding the suffix
// causes unnecessary cache misses and refetches on team switch.
//
// Rule: every orgProcedure and authProcedure query belongs here (team scope, if
// any, is always in the input). teamProcedure and multiTeamProcedure queries
// are intentionally excluded — their team scope comes solely from the header.
// Mobile routes are exempt (handled separately outside this mechanism).
// When adding a new orgProcedure query, see the CodeRabbit rule in .coderabbit.yaml.
const ORG_SCOPED_PROCEDURE_PATHS = new Set([
  // Permissions (org-wide)
  'permissions.coaches',
  'permissions.teams',
  'permissions.permissionsForProfile',
  'permissions.myPermissions',
  // Roster / teams
  'roster.teamsPreview',
  'roster.athleteProfile',
  // Org settings
  'orgSettings.orgDetail',
  // User / auth identity
  'user.profilesForSwitch',
  'user.reportingUser',
  'user.getEffectiveRole',
  'user.mavenSignedUserData',
  // Axle org/person documents (team document intentionally excluded — teamProcedure)
  'admin.axleOrgDocument',
  'admin.axlePersonDocument',
  // Library data (org-wide)
  'metricsLibrary.allMetrics',
  'metricsLibrary.categories',
  'metricsLibrary.metricsSearch',
  'schemeLibrary.trainingFocuses',
  'schemeLibrary.searchSchemes',
  'exerciseLibrary.getIntegrationProviders',
  // Tags (org-wide)
  'tags.tags',
  'tags.searchTags',
  'tags.hasTagWithNameAndType',
  'tags.canBeDeleted',
  // Name lookup tables (org-wide)
  'trainingType.fetch',
  'trainingType.search',
  'bodyPart.fetch',
  'bodyPart.search',
  'equipment.fetch',
  'equipment.search',
  // Block queries (blockId in input differentiates cache entries)
  'block.blockMetadata',
  'block.blockForDraft',
  'block.getOverlappingWorkouts',
  'block.getPublishedBlock',
  'block.getPublishedBlockByVersion',
  'block.getPublishedAthleteBlock',
  'block.getPublishedAthleteBlockByVersion',
  // Team / plan (ids in input)
  'teamSettings.blockPrintSettings',
  'plan.trackAthletes',
  'display.getSelection',
  // Athlete benchmarks (profileId / benchmarkGridId in input)
  'athleteBenchmarks.fetchAllExerciseBenchmarksForAthlete',
  'athleteBenchmarks.fetchAthleteExerciseBenchmarks',
  'athleteBenchmarks.fetchBenchmarkHistoryForExercise',
  'athleteKpiBenchmarks.fetchKpiBenchmarkHistory',
  'athleteProfiles.getExerciseBenchmarks',
  // Sync (syncGroups / fromSyncId in input)
  'sync.bootstrap',
  'sync.deltaSync',
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Team-scoped tRPC queries include the active tw-team-ids header value in
      // their hash so Team A and Team B use separate cache entries — no stale
      // cross-team data. Reading from getTeamIdsHeader() means web (URL-based)
      // and tablet (setTabletTeamIdHeader) both work without extra wiring.
      // Org-scoped procedures share one cache entry across teams (no refetch).
      // Non-tRPC keys (e.g. SESSION_QUERY_KEY = ['session']) are unaffected.
      queryKeyHashFn: (queryKey) => {
        if (Array.isArray(queryKey[0])) {
          const path = (queryKey[0] as string[]).join('.');
          if (!ORG_SCOPED_PROCEDURE_PATHS.has(path)) {
            const teamKey = getTeamIdsHeader()['tw-team-ids'] ?? '';
            return hashQueryKey([...queryKey, teamKey]);
          }
        }
        return hashQueryKey(queryKey);
      },
      useErrorBoundary: isClientRoutePermissionError,
      retry: (failureCount, error) => {
        if (isClientRoutePermissionError(error)) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

// Tab guards (SC-4792) re-read myPermissions through fetchQuery (the tRPC
// utils .fetch wrapper). In our query-core version ensureData/ensureQueryData
// ignores staleTime — it returns any cached value unconditionally — so the
// stale time here would be dead config. fetchQuery honors it: in-area tab
// navigation stays on the cache instead of one request per click, while
// still picking up revocations within a minute.
const PERMISSIONS_STALE_TIME_MS = 60_000;

/**
 * App shell: mounts the query client, Zero, viewer, and nav providers and
 * injects the router context (auth, flags, selected team, cached-permission
 * read) used by the route guards.
 */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LocalizationProvider
          dateAdapter={AdapterLuxon}
          adapterLocale={getLocale()}
        >
          <JotaiProvider store={jotaiStore}>
            <InnerApp />
            <Toaster />
          </JotaiProvider>
        </LocalizationProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function InnerApp() {
  const auth = useAuth();
  const userId = auth.auth?.userId;
  // Only org sessions have teams to select (ViewerProvider only fetches
  // teamsPreview for auth.type === 'org'); orgId also scopes the storage key
  // per-org, since a userId can back Profiles in multiple orgs (app switcher)
  // but Team ids are org-scoped.
  const orgId = auth.auth?.type === 'org' ? auth.auth.orgId : undefined;

  // SC-4235: seed from localStorage so a returning user lands back on their
  // last-selected team instead of the teams[0] fallback in router.tsx.
  const [selectedTeam, setSelectedTeamState] = useState<string>(() =>
    readStoredSelectedTeam(userId, orgId),
  );

  // auth resolves asynchronously; if this component's first render happens
  // before it does (e.g. right after login, without a full page reload),
  // pick up the persisted team once the user/org id becomes available. Sync
  // unconditionally (including '') — otherwise a scope change into an org
  // with no stored team yet would leave the previous org's team id in state.
  useEffect(() => {
    setSelectedTeamState(readStoredSelectedTeam(userId, orgId));
  }, [userId, orgId]);

  const setSelectedTeam = useCallback(
    (teamId: string) => {
      setSelectedTeamState(teamId);
      writeStoredSelectedTeam(userId, orgId, teamId);
    },
    [userId, orgId],
  );

  const trpcReactClientRef =
    useRef<ReturnType<(typeof trpc)['createClient']>>();
  if (!trpcReactClientRef.current) {
    trpcReactClientRef.current = trpc.createClient(
      getTrpcClientOptions(() => auth.setAuth(null)),
    );
  }

  return (
    <trpc.Provider
      client={trpcReactClientRef.current}
      queryClient={queryClient}
    >
      <FeatureFlagsProvider authContext={auth}>
        <RouterWithFlags
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
          auth={auth}
        />
      </FeatureFlagsProvider>
    </trpc.Provider>
  );
}

/**
 * Inner app component gated on the feature-flag subscription, so the router
 * context (and its guards) only rebuilds when the flags actually change.
 */
function RouterWithFlags({
  selectedTeam,
  setSelectedTeam,
  auth,
}: {
  selectedTeam: string;
  setSelectedTeam: (team: string) => void;
  auth: ReturnType<typeof useAuth>;
}) {
  const flags = useFlags();
  const utils = trpc.useUtils();

  useEffect(() => initPageTracking(router), []);

  return (
    <ZeroClientProvider>
      <ViewerProvider
        selectedTeam={selectedTeam}
        setSelectedTeam={setSelectedTeam}
      >
        <NavProvider
          Link={({ to, children, className, ...props }) => (
            <Link
              to={to}
              params={{}}
              className={cn(className, 'text-white')}
              {...props}
            >
              {children}
            </Link>
          )}
        >
          <RouterProvider
            router={router}
            context={{
              ...auth,
              selectedTeam,
              setSelectedTeam,
              flags,
              ensurePermissions: async () => {
                const { permissions } =
                  await utils.permissions.myPermissions.fetch(undefined, {
                    staleTime: PERMISSIONS_STALE_TIME_MS,
                  });
                return permissions;
              },
            }}
          />
        </NavProvider>
      </ViewerProvider>
    </ZeroClientProvider>
  );
}

export default App;
